import type { ValueAttribution } from '@/services/dossierContext'
import { splitStamp } from './activityNarrative'
import clsx from 'clsx'
import type { ReactNode } from 'react'
import { FileText, Info } from 'lucide-react'
import { WorkspacePanel } from './WorkspacePanel'
import { RelationshipMapPanel } from './RelationshipMapPanel'
import {
  CashFlowSummaryContent,
  cashFlowSummaryTitle,
  type useEconomicsModel,
} from '../EconomicsPage'
import { useClientProfile } from '@/hooks/useClientProfile'
import { ValueStateChip } from '../valueStateChip'
import { EditableValue, showsStandIn } from '../EditableValue'
import { useDossierEdit } from '../editContext'
import { LeverageCard } from '../LeverageCard'
import { DETAIL_VERB } from './sourceRoute'
import type { LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'
import type { RawDealFile } from '@/services/dealFile'
import type {
  CommunicationRow,
  ConditionRow,
  DecisionRecord,
  JournalEntry,
  NextActionRow,
  PartyNode,
  RiskRow,
  SourcedDatapoint,
  UnderwritingChangeRow,
} from './types'

function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="py-2 text-center text-[10px] text-stone-600">{children}</p>
  )
}

/**
 * Whose figure this is, said on the value itself.
 *
 * A sponsor's stabilized NOI rendered in the same type as the firm's own
 * conclusion reads as the deal's stabilized NOI, which quietly answers the
 * question the firm's underwriting exists to ask. The badge is the answer to
 * "says who".
 */
const ATTRIBUTION_CHIP: Record<
  Exclude<ValueAttribution, 'unattributed'>,
  { label: string; title: (cellRef?: string | null) => string; className: string }
> = {
  // SPONSOR only when the sponsor's own underwriting model is the source. A
  // broker's email carried the sponsor's name on every figure (OVR-25).
  sponsor: {
    label: 'Sponsor',
    title: (cellRef) =>
      `From the sponsor's own underwriting model${cellRef ? ` — ${cellRef}` : ''}. Not yet verified by this firm.`,
    className: 'rounded bg-amber-500/10 px-1 py-0.5 text-[7.5px] uppercase tracking-wide text-amber-400/90',
  },
  broker: {
    label: 'Broker',
    title: () => "Stated by the broker, or in a document the broker sent. Not yet verified by this firm.",
    className: 'rounded bg-stone-500/15 px-1 py-0.5 text-[7.5px] uppercase tracking-wide text-stone-300',
  },
  external: {
    label: 'Outside',
    title: () => 'Stated by a party outside the firm. Not yet verified by this firm.',
    className: 'rounded bg-stone-500/15 px-1 py-0.5 text-[7.5px] uppercase tracking-wide text-stone-300',
  },
  client: {
    // The tenant's own short name is read from the client profile at render
    // (ClientChipLabel); this literal is only the neutral fallback, never
    // another client's name.
    label: 'Firm',
    title: () => "This firm's own figure.",
    className: 'rounded bg-sky-400/10 px-1 py-0.5 text-[7.5px] uppercase tracking-wide text-[#77c3f4]',
  },
}

/** A portfolio's figure rolled up from its properties' sponsor models: the
 *  sponsor's chip, saying how the figure was reached rather than naming a cell. */
const ROLLUP_CHIP = {
  label: 'Roll-up',
  title:
    "The portfolio roll-up of the sponsor's own underwriting models, one per property: a sum, or a ratio of the summed figures. Not yet verified by this firm.",
  className: ATTRIBUTION_CHIP.sponsor.className,
}

/** The tenant's own name on its figures, from the client profile — never a
 *  literal. Until the profile answers, the chip says only 'Firm'. */
function ClientChipLabel({ fallback }: { fallback: string }) {
  const profile = useClientProfile()
  return <>{profile.shortName || profile.name || fallback}</>
}

export { ValueStateChip }

export function AttributionBadge({
  attribution,
  cellRef,
  disputed,
  rollup = false,
}: {
  attribution?: ValueAttribution | null
  cellRef?: string | null
  disputed?: boolean
  /** The sponsor's figure is a portfolio's roll-up of its properties' models. */
  rollup?: boolean
}) {
  if (!attribution || attribution === 'unattributed') return null
  const chip = ATTRIBUTION_CHIP[attribution]
  if (!chip) return null
  const shown = rollup && attribution === 'sponsor'
    ? ROLLUP_CHIP
    : { label: chip.label, title: chip.title(cellRef), className: chip.className }
  return (
    <span className="inline-flex items-center gap-1">
      <span title={shown.title} className={shown.className}>
        {attribution === 'client' ? <ClientChipLabel fallback={shown.label} /> : shown.label}
      </span>
      {disputed ? (
        <span
          title="This firm's conclusion differs from what was represented to it."
          className="rounded bg-rose-500/10 px-1 py-0.5 text-[7.5px] uppercase tracking-wide text-rose-400/90"
        >
          Differs
        </span>
      ) : null}
    </span>
  )
}

function SeverityBadge({
  severity,
}: {
  severity: 'high' | 'medium' | 'low' | null
}) {
  if (!severity) return <span className="text-[9px] text-stone-600">—</span>
  return (
    <span
      className={clsx(
        'rounded px-1.5 py-0.5 text-[8px] font-semibold uppercase',
        severity === 'high' && 'bg-red-500/15 text-red-300',
        severity === 'medium' && 'bg-amber-500/15 text-amber-200',
        severity === 'low' && 'bg-stone-500/15 text-stone-300',
      )}
    >
      {severity}
    </span>
  )
}

function StatusBadge({ status }: { status: ConditionRow['status'] }) {
  return (
    <span
      className={clsx(
        'rounded px-1.5 py-0.5 text-[8px] font-semibold uppercase',
        status === 'cleared' && 'bg-emerald-500/15 text-emerald-300',
        status === 'overdue' && 'bg-red-500/15 text-red-300',
        status === 'open' && 'bg-amber-500/15 text-amber-200',
      )}
    >
      {status === 'cleared'
        ? 'Satisfied'
        : status === 'overdue'
          ? 'Overdue'
          : 'Open'}
    </span>
  )
}

/** Left column — Decision Snapshot */
export function DecisionSnapshotColumn({
  loanOverview,
  propertyFields,
  raw,
  loanId,
  economics = null,
  cashFlowLineage = [],
  onSourceClick,
  onFieldDetail,
  onViewCapitalStack,
}: {
  loanOverview: SourcedDatapoint[]
  propertyFields: SourcedDatapoint[]
  parties: PartyNode[]
  /** The deal file and its id, for the Leverage & Capitalization card. */
  raw?: RawDealFile
  loanId?: string
  /** The Economics tab's model; its cash-flow summary sits under the stack. */
  economics?: ReturnType<typeof useEconomicsModel> | null
  /** The deal's lineage, so a derived cash-flow line (debt service from
   *  NOI / DSCR) says it is derived, and the card's figures say whose. */
  cashFlowLineage?: LoanFieldLineageField[]
  onSourceClick?: (fieldName: string | null) => void
  /** A figure no document states (Derived, Suggested, Confirmed): show its
   *  formula and inputs, its basis, or who confirmed it. */
  onFieldDetail?: (fieldName: string) => void
  onViewParties?: () => void
  /** Opens the Capital Stack tab, where the full capitalization lives. */
  onViewCapitalStack?: () => void
}) {
  // h-full: the column fills its grid area on tall screens (Property Snapshot
  // and Capital Stack grow) and scrolls on short ones.
  return (
    <div className="overview-snapshot grid h-full min-h-0 grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)] gap-1.5 overflow-auto">
      <div className="flex min-w-0 flex-col gap-1.5">
        <WorkspacePanel title="Loan Overview" dense className="shrink-0">
          <MetricTable rows={loanOverview} onSourceClick={onSourceClick} onFieldDetail={onFieldDetail} />
        </WorkspacePanel>

        <WorkspacePanel
          title="Property Snapshot"
          dense
          className="flex-[1_0_auto]"
        >
          <MetricTable rows={propertyFields} onSourceClick={onSourceClick} onFieldDetail={onFieldDetail} />
        </WorkspacePanel>
      </div>
      {/* Leverage & capitalization over the in-place cash flow. The card
          takes the height its content needs and the cash flow the rest: two
          equal halves (auto-rows-fr) stretched the card to the cash flow's
          height, so at 1366x768 its bottom sat below the fold and the reader
          scrolled the column to see the whole of it. */}
      <div className="grid min-h-0 min-w-0 grid-rows-[auto_minmax(0,1fr)] gap-1.5">
        <WorkspacePanel
          title="Leverage & Capitalization"
          dense
          className="overview-capital-chart"
          // "View", not the tab's name: at 1366 the longer label wrapped the
          // header onto two lines in a 238px column.
          action={onViewCapitalStack ? 'View' : undefined}
          onAction={onViewCapitalStack}
        >
          {raw && loanId ? (
            <LeverageCard raw={raw} loanId={loanId} lineage={cashFlowLineage} />
          ) : (
            <Empty>No capital figures on record.</Empty>
          )}
        </WorkspacePanel>
        {economics ? (
          <WorkspacePanel title={cashFlowSummaryTitle(economics)} dense>
            <CashFlowSummaryContent model={economics} dense lineage={cashFlowLineage} />
          </WorkspacePanel>
        ) : null}
      </div>
    </div>
  )
}

function MetricTable({
  rows,
  onSourceClick,
  onFieldDetail,
}: {
  rows: SourcedDatapoint[]
  onSourceClick?: (fieldName: string | null) => void
  onFieldDetail?: (fieldName: string) => void
}) {
  const edit = useDossierEdit()
  return (
    <table className="w-full border-collapse text-[10px]">
      <thead>
        <tr className="border-b border-stone-800 text-center text-stone-600">
          <th className="py-1 pr-2 font-normal">Metric</th>
          <th className="py-1 pr-2 font-normal">Value</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          /* Only a stated figure links to a document. A derived, suggested
             or confirmed one has no file to open: it opens its formula and
             inputs, basis or confirmation instead. */
          const value =
            row.source?.state && DETAIL_VERB[row.source.state] && (row.source.fieldName ?? row.fieldName) ? (
              onFieldDetail ? (
                <button
                  type="button"
                  className="underline decoration-stone-500 decoration-dotted underline-offset-2 hover:decoration-stone-300"
                  title={row.source.label}
                  aria-label={`Show how ${row.label} ${DETAIL_VERB[row.source.state]}: ${row.value}`}
                  onClick={() => onFieldDetail((row.source?.fieldName ?? row.fieldName)!)}
                >
                  {row.value}
                </button>
              ) : (
                row.value
              )
            ) : row.source && onSourceClick ? (
              <button
                type="button"
                className="text-[#77c3f4] underline decoration-sky-400/40 underline-offset-2 hover:decoration-sky-300"
                title={`Open source: ${row.source.label}`}
                aria-label={`Open source for ${row.label}: ${row.value}`}
                onClick={() => onSourceClick(row.source?.fieldName ?? row.fieldName)}
              >
                {row.value}
              </button>
            ) : (
              row.value
            )
          const marks =
            row.value !== '—' ? (
              <>
                <ValueStateChip source={row.source} />
                <AttributionBadge
                  attribution={row.source?.attribution}
                  cellRef={row.source?.cellRef}
                  disputed={row.source?.disputed}
                  rollup={row.source?.rollup}
                />
              </>
            ) : row.source?.state === 'in_unread_model' ? (
              // A blank an attached, unread model may fill says so.
              <ValueStateChip source={row.source} />
            ) : null
          // Unlocked, the value is an input and cannot also be the link to
          // its document (or its basis): the same action as a small control
          // after the chips, so provenance stays one click away while the
          // figure is being overwritten.
          const detailField = row.source?.fieldName ?? row.fieldName
          const sourceControl =
            row.value === '—' || !row.source
              ? null
              : row.source.state && DETAIL_VERB[row.source.state] && detailField
                ? onFieldDetail ? (
                    <button
                      type="button"
                      data-value-source=""
                      className="inline-flex shrink-0 text-stone-500 hover:text-stone-200"
                      title={row.source.label}
                      aria-label={`Show how ${row.label} ${DETAIL_VERB[row.source.state]}`}
                      onClick={() => onFieldDetail(detailField)}
                    >
                      <Info size={10} aria-hidden="true" />
                    </button>
                  ) : null
                : onSourceClick ? (
                    <button
                      type="button"
                      data-value-source=""
                      className="inline-flex shrink-0 text-[#77c3f4] hover:text-sky-200"
                      title={`Open source: ${row.source.label}`}
                      aria-label={`Open source for ${row.label}`}
                      onClick={() => onSourceClick(row.source?.fieldName ?? row.fieldName)}
                    >
                      <FileText size={10} aria-hidden="true" />
                    </button>
                  ) : null
          // A figure the row prints while its field holds nothing — the
          // request as initial plus future funding, a WALT read from another
          // key, a coupon from the pipeline row — is not that field's value:
          // an input there would show an empty box where the figure was, and
          // typing into it would replace a computed figure with a hand-typed
          // one (R1). It reads as computed and opens what it is made of.
          const computed =
            row.source?.state === 'derived'
              ? row.source.formula ?? ''
              : row.fieldName && !row.edit && showsStandIn(edit, row.fieldName, row.value)
                ? 'shown from other figures while this field itself is empty'
                : undefined
          return (
            <tr key={row.label} className="border-b border-stone-900/80">
              {/* One line per label. The table sizes its columns by their
                  content, so a label that could wrap gave way to whatever the
                  value column held: unlocked, the inputs and the source
                  control widened that column by 2px at 1366 and "Property
                  Type" and "NOI (In-Place)" broke onto second lines, growing
                  the panel 30px. Unbreakable, the labels hold their column. */}
              <td className="whitespace-nowrap py-1 pr-2 text-stone-500">{row.label}</td>
              <td className="py-1 pr-2 text-center font-mono text-stone-100">
                <span className="inline-flex items-center gap-1.5">
                  {/* Unlocked, the value is the field it names, edited in
                      place (owner, 21 Oct) — a derived one opens its inputs,
                      and a composed one its fields. Locked, the cell is the
                      source link and chips it always was. */}
                  {row.fieldName ? (
                    <EditableValue
                      name={row.fieldName}
                      label={row.label}
                      display={row.value}
                      marks={marks}
                      source={sourceControl}
                      fields={row.edit}
                      computed={computed}
                      inputs={row.inputs}
                      readOnlyReason={row.readOnlyReason}
                    >
                      {value}
                      {marks}
                    </EditableValue>
                  ) : (
                    <>
                      {value}
                      {marks}
                    </>
                  )}
                </span>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

/** Center column — Committee / Decision Record */
export function DecisionRecordColumn({
  decision,
  conditions,
  underwritingChanges,
  risks,
  strengths = [],
  journal,
  onViewActivity,
}: {
  decision: DecisionRecord
  conditions: ConditionRow[]
  underwritingChanges: UnderwritingChangeRow[]
  risks: RiskRow[]
  strengths?: string[]
  journal: JournalEntry[]
  onViewActivity?: () => void
}) {
  const approved = decision.headline.toLowerCase().includes('approved')
  // Fixed panels keep their content height; the ledger takes whatever is left
  // so the column always reaches the bottom of the workspace.
  return (
    <div className="flex h-full min-h-0 flex-col gap-1.5 overflow-auto">
      <WorkspacePanel title="Committee · Decision Record" dense className="shrink-0">
        <div
          className={clsx(
            'rounded border px-3 py-2',
            approved
              ? 'border-amber-400/45 bg-amber-400/[0.08]'
              : 'border-stone-700 bg-black/30',
          )}
        >
          <div className="text-[13px] font-semibold text-amber-100">
            {decision.headline}
          </div>
          {decision.date ? (
            <div className="mt-0.5 text-[10px] text-stone-500">
              {decision.date}
            </div>
          ) : null}
          {decision.body ? (
            <p className="mt-1 text-[10px] text-stone-400">{decision.body}</p>
          ) : null}
        </div>

        {decision.decided === false ? (
          // No committee has seen the deal: a grid of "Not yet approved" and
          // the request's own rate read as a decision (22 Sep review: tables
          // for unbuilt stages hidden). One line says where it stands.
          <p data-testid="decision-pending" className="mt-2 text-[10px] text-stone-400">
            No committee decision yet — {decision.conditionsOpen}{' '}
            {decision.conditionsOpen === 1 ? 'condition' : 'conditions'} open
            {decision.nextGate ? <> · Next gate: <span className="text-stone-300">{decision.nextGate}</span></> : null}
          </p>
        ) : (
        <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {[
            { label: 'Approved Amount', value: decision.approvedAmount ?? '—' },
            { label: 'Max LTV', value: decision.maxLtv ?? '—' },
            { label: 'Rate', value: decision.rate ?? '—' },
            {
              label: 'Conditions',
              value: `${decision.conditionsOpen} open / ${decision.conditionsCleared} satisfied`,
            },
            { label: 'Next Gate', value: decision.nextGate ?? '—' },
          ].map((m) => (
            <div
              key={m.label}
              className="rounded border border-stone-800/80 bg-black/30 px-2 py-1.5 text-center"
            >
              <div className="font-mono text-[8px] uppercase tracking-wide text-amber-300/80">
                {m.label}
              </div>
              <div className="mt-0.5 truncate text-[11px] font-medium text-stone-100">
                {m.value}
              </div>
            </div>
          ))}
        </div>
        )}
      </WorkspacePanel>

      <WorkspacePanel title="Conditions Precedent" dense className="shrink-0">
        {conditions.length === 0 ? (
          <Empty>No conditions recorded on this deal.</Empty>
        ) : (
          <table className="w-full border-collapse text-[9px]">
            <thead>
              <tr className="border-b border-stone-800 text-center text-stone-600">
                <th className="py-1 pr-1 font-normal">#</th>
                <th className="py-1 pr-1 font-normal">Condition</th>
                <th className="py-1 pr-1 font-normal">Owner</th>
                <th className="py-1 pr-1 font-normal">Due</th>
                <th className="py-1 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {conditions.map((c, i) => (
                <tr
                  key={`${c.label}-${i}`}
                  className="border-b border-stone-900/80"
                >
                  <td className="py-1 pr-1 text-stone-600">{i + 1}</td>
                  <td className="py-1 pr-1 text-stone-300">{c.label}</td>
                  <td className="py-1 pr-1 text-stone-500">{c.owner ?? '—'}</td>
                  <td className="py-1 pr-1 font-mono text-stone-400">
                    {c.due ?? '—'}
                  </td>
                  <td className="py-1">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </WorkspacePanel>

      <WorkspacePanel title="Key Underwriting Changes" dense className="shrink-0">
        {underwritingChanges.length === 0 ? (
          <Empty>No entry-vs-current underwriting deltas on record.</Empty>
        ) : (
          <table className="w-full border-collapse text-[9px]">
            <thead>
              <tr className="border-b border-stone-800 text-center text-stone-600">
                <th className="py-1 pr-1 font-normal">Metric</th>
                <th className="py-1 pr-1 font-normal">Entry</th>
                <th className="py-1 pr-1 font-normal">Current</th>
                <th className="py-1 font-normal">Change</th>
              </tr>
            </thead>
            <tbody>
              {underwritingChanges.map((row) => (
                <tr key={row.metric} className="border-b border-stone-900/80">
                  <td className="py-1 pr-1 text-stone-300">{row.metric}</td>
                  <td className="py-1 pr-1 font-mono text-stone-400">
                    {row.entry}
                  </td>
                  <td className="py-1 pr-1 font-mono text-stone-200">
                    {row.current}
                  </td>
                  <td
                    className={clsx(
                      'py-1 font-mono',
                      row.direction === 'up' && 'text-emerald-400',
                      row.direction === 'down' && 'text-red-300',
                      (!row.direction || row.direction === 'flat') &&
                        'text-stone-400',
                    )}
                  >
                    {row.change ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </WorkspacePanel>

      <WorkspacePanel title="Top Risks & Mitigants" dense className="shrink-0">
        {strengths.length > 0 ? (
          <div className="mb-2" data-testid="credit-strengths">
            <div className="mb-1 font-mono text-[8px] uppercase tracking-wide text-stone-600">
              Strengths
            </div>
            <ul className="space-y-0.5 text-[9px] text-stone-300">
              {strengths.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {risks.length === 0 ? (
          <Empty>No open risks recorded on this deal.</Empty>
        ) : (
          <table className="w-full border-collapse text-[9px]">
            <thead>
              <tr className="border-b border-stone-800 text-center text-stone-600">
                <th className="py-1 pr-1 font-normal">Risk</th>
                <th className="py-1 pr-1 font-normal">Mitigant</th>
                <th className="py-1 font-normal">Severity</th>
              </tr>
            </thead>
            <tbody>
              {risks.map((r) => (
                <tr
                  key={r.risk}
                  className="border-b border-stone-900/80 align-top"
                >
                  <td className="py-1 pr-1 text-stone-300">{r.risk}</td>
                  <td className="py-1 pr-1 text-stone-500">{r.mitigation}</td>
                  <td className="py-1">
                    <SeverityBadge severity={r.severity} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </WorkspacePanel>

      <WorkspacePanel
        title="Stage Event Ledger"
        className="flex-[1_0_auto]"
        action="View all"
        onAction={onViewActivity}
        dense
      >
        {journal.length === 0 ? (
          <Empty>No stage events recorded yet.</Empty>
        ) : (
          /* A table, because this is a log a person scans down: when, what, who.
             It previously rendered the event_type verbatim over a JSON dump of
             the details blob, which reads as a database row rather than a
             record of what happened. */
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-stone-800">
                  {['Date', 'Time', 'Event', 'Who'].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="whitespace-nowrap py-1 pr-3 text-center font-mono text-[8px] uppercase tracking-wider text-stone-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {journal.slice(0, 10).map((e) => {
                  const { date, time } = splitStamp(e.at)
                  return (
                    <tr
                      key={e.id}
                      className="border-b border-stone-900/70 last:border-0 align-top"
                    >
                      <td className="whitespace-nowrap py-1 pr-3 font-mono text-[9px] text-stone-400">
                        {date}
                      </td>
                      <td className="whitespace-nowrap py-1 pr-3 font-mono text-[9px] text-stone-500">
                        {time}
                      </td>
                      <td className="py-1 pr-3 text-[10px] text-stone-200">
                        {e.title}
                        {e.summary ? (
                          <span className="block text-[9px] text-stone-500">
                            {e.summary}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-1 text-[9px] text-stone-400">
                        {e.actor || '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </WorkspacePanel>
    </div>
  )
}

/** Right column upper — team, contacts, actions, communications */
export function RelationshipSidebar({
  loanId,
  onOpenParties,
  nextActions,
  communications,
}: {
  loanId: string
  onOpenParties?: () => void
  nextActions: NextActionRow[]
  communications: CommunicationRow[]
}) {
  return (
    <div className="flex h-full min-h-0 flex-col gap-1.5 overflow-auto">
      {/* The Parties tab's map in place of the Deal Team table and party cards;
          it takes the rail's spare height and fits every party inside it. */}
      <RelationshipMapPanel
        loanId={loanId}
        onOpenParties={onOpenParties}
        className="!min-h-[200px] flex-1"
      />

      <WorkspacePanel title={`Next Actions (${nextActions.length})`} dense className="shrink-0">
        {nextActions.length === 0 ? (
          <Empty>No open next actions on this deal.</Empty>
        ) : (
          <table className="w-full border-collapse text-[9px]">
            <thead>
              <tr className="border-b border-stone-800 text-center text-stone-600">
                <th className="py-1 pr-1 font-normal">Action</th>
                <th className="py-1 pr-1 font-normal">Owner</th>
                <th className="py-1 font-normal">Due</th>
              </tr>
            </thead>
            <tbody>
              {nextActions.map((a) => (
                <tr key={a.action} className="border-b border-stone-900/80">
                  <td className="py-1 pr-1 text-stone-300">{a.action}</td>
                  <td className="py-1 pr-1 text-stone-500">{a.owner ?? '—'}</td>
                  <td className="py-1 font-mono text-stone-400">
                    {a.due ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </WorkspacePanel>

      <WorkspacePanel title="Latest Communications" dense className="shrink-0">
        {communications.length === 0 ? (
          <Empty>No communications logged yet.</Empty>
        ) : (
          /* Sender, recipient and subject — a communication naming neither end
             is a log line, not a record of a conversation. */
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-stone-800">
                  {['Date', 'Time', 'Type', 'From', 'To', 'Subject'].map(
                    (h) => (
                      <th
                        key={h}
                        scope="col"
                        className="whitespace-nowrap py-1 pr-3 text-center font-mono text-[8px] uppercase tracking-wider text-stone-500"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {communications.map((c, i) => {
                  const { date, time } = splitStamp(c.at)
                  return (
                    <tr
                      key={`${c.at}-${i}`}
                      className="border-b border-stone-900/70 last:border-0 align-top"
                    >
                      <td className="whitespace-nowrap py-1 pr-3 font-mono text-[9px] text-stone-400">
                        {date}
                      </td>
                      <td className="whitespace-nowrap py-1 pr-3 font-mono text-[9px] text-stone-500">
                        {time}
                      </td>
                      <td className="whitespace-nowrap py-1 pr-3 text-[9px] uppercase text-amber-200/70">
                        {c.kind}
                      </td>
                      <td
                        className="max-w-[9rem] truncate py-1 pr-3 text-[9px] text-stone-400"
                        title={c.from ?? ''}
                      >
                        {c.from || '—'}
                      </td>
                      <td
                        className="max-w-[9rem] truncate py-1 pr-3 text-[9px] text-stone-400"
                        title={c.to ?? ''}
                      >
                        {c.to || '—'}
                      </td>
                      <td
                        className="py-1 text-[10px] text-stone-200"
                        title={c.subject}
                      >
                        {c.subject}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </WorkspacePanel>
    </div>
  )
}

/** @deprecated Prefer RelationshipSidebar + InspectorLogCompose dock */
export function RelationshipActivityColumn(
  props: Parameters<typeof RelationshipSidebar>[0] & {
    logKind?: 'call' | 'meeting' | 'email' | 'note'
    onLogKind?: (k: 'call' | 'meeting' | 'email' | 'note') => void
    subject?: string
    onSubject?: (v: string) => void
    notes?: string
    onNotes?: (v: string) => void
    onSave?: () => void
    saveDisabled?: boolean
    saving?: boolean
  },
) {
  return (
    <RelationshipSidebar
      loanId={props.loanId}
      onOpenParties={props.onOpenParties}
      nextActions={props.nextActions}
      communications={props.communications}
    />
  )
}
